import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { HonorarioDialogo, PericiaDialogo, PrazoDialogo, TarefaDialogo } from "../componentes/PeritoDialogos";
import { DocumentosDaPericia } from "../componentes/PeritoDocumentos";
import { PartesEditor } from "../componentes/PeritoEditores";
import {
  BOTAO_PERIGO, BOTAO_SECUNDARIO, Chip, Dialogo, EtapaProgresso, IconeEstrela, IconeMais, ProximaAcaoInline, Rotulo,
} from "../componentes/PeritoUi";
import { CAMPO } from "../componentes/ui";
import { diasAte, formatarData, formatarDataHora } from "../lib/perito/datas";
import { formatBRL, PAGAMENTO_LABEL, PAGAMENTO_TOM, PRIORIDADE_LABEL, PRIORIDADE_TOM } from "../lib/perito/format";
import { TEMPLATES_TAREFA } from "../lib/perito/modelos";
import { ETAPAS, ETAPA_LABEL, etapaNumDe, type EtapaNum } from "../lib/perito/pipeline";
import { rotuloDias, tomDosDias } from "../lib/perito/proximaAcao";
import { usePerito } from "../lib/perito/store";
import type { Honorario, Parte, Prazo, Tarefa } from "../lib/perito/tipos";
import { nomeDaPericia } from "./PeritoPericias";

// A ficha de uma perícia (000385), portada de `src/routes/_authenticated/pericias.$id.tsx` da Central do
// Perito: a etapa, a próxima ação, os prazos, as partes, as tarefas com o checklist, os honorários e o
// histórico. A aba está no ENDEREÇO (`?aba=tarefas`), para o aluno voltar pelo navegador e mandar o link de
// uma aba. A aba de documentos é da fase C (000386): o despacho lido no cadastro chega aqui.

const RAIZ = "/ferramenta/central-do-perito";
const ABAS = ["prazos", "partes", "tarefas", "financeiro", "documentos", "historico"] as const;
type Aba = (typeof ABAS)[number];

// As partes editam no lugar e avisam a cada tecla. Cada letra seria uma gravação e uma linha de "Partes
// atualizadas" no histórico: a tela responde na hora, e a gravação sai quando a digitação para por este tempo
// (como lá).
const ESPERA_PARTES_MS = 800;

/** Um fato sobre o navy: cor de marca, porque ali o fundo é escuro nos dois temas. */
function Fato({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-cf-lg bg-white/10 px-4 py-3">
      <span className="block text-[11px] font-semibold tracking-[0.16em] text-navy-mute uppercase">{rotulo}</span>
      <div className="mt-1 text-sm font-semibold text-white tabular-nums">{children}</div>
    </div>
  );
}

function Secao({ titulo, descricao, acao, children }: { titulo: string; descricao?: string; acao?: ReactNode; children: ReactNode }) {
  return (
    <section className="surface p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-titulo">{titulo}</h2>
          {descricao && <p className="mt-0.5 text-sm text-cinza">{descricao}</p>}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

const BOTAO_ICONE = "grid size-9 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-titulo disabled:opacity-55";

export default function PeritoFicha({ id }: { id: string }) {
  const s = usePerito();
  const navegar = useNavigate();
  const [busca, setBusca] = useSearchParams();
  const abaDaBusca = busca.get("aba");
  const aba: Aba = (ABAS as readonly string[]).includes(abaDaBusca ?? "") ? (abaDaBusca as Aba) : "prazos";
  // A troca de aba mexe só na `aba`: o `?curso=` que a barra de volta lê continua no endereço.
  const trocarAba = (a: Aba) => {
    const nova = new URLSearchParams(busca);
    if (a === "prazos") nova.delete("aba");
    else nova.set("aba", a);
    setBusca(nova, { replace: true });
  };

  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [prazoEdit, setPrazoEdit] = useState<Prazo | null>(null);
  const [prazoNovo, setPrazoNovo] = useState(false);
  const [tarefaEdit, setTarefaEdit] = useState<Tarefa | null>(null);
  const [tarefaNova, setTarefaNova] = useState(false);
  const [honorEdit, setHonorEdit] = useState<Honorario | null>(null);
  const [honorNovo, setHonorNovo] = useState(false);
  const [novoPrazoTitulo, setNovoPrazoTitulo] = useState("");
  const [novoPrazoData, setNovoPrazoData] = useState("");
  const [novaTarefa, setNovaTarefa] = useState("");
  const [partesEmEdicao, setPartesEmEdicao] = useState<Parte[] | null>(null);
  const partesPendentes = useRef<Parte[] | null>(null);
  const relogio = useRef<number | undefined>(undefined);

  const gravarPartes = () => {
    window.clearTimeout(relogio.current);
    const partes = partesPendentes.current;
    if (!partes) return;
    partesPendentes.current = null;
    const { updatePericia, addHistorico } = usePerito.getState();
    updatePericia(id, { partes: partes.filter((x) => x.nome.trim()) });
    addHistorico({ acao: "Partes atualizadas", pericia_id: id });
    setPartesEmEdicao(null);
  };
  const alterarPartes = (partes: Parte[]) => {
    partesPendentes.current = partes;
    setPartesEmEdicao(partes);
    window.clearTimeout(relogio.current);
    relogio.current = window.setTimeout(gravarPartes, ESPERA_PARTES_MS);
  };
  // Saiu da ficha no meio da digitação: grava o que estava pendente.
  useEffect(
    () => () => {
      window.clearTimeout(relogio.current);
      const partes = partesPendentes.current;
      if (!partes) return;
      const { updatePericia, addHistorico } = usePerito.getState();
      updatePericia(id, { partes: partes.filter((x) => x.nome.trim()) });
      addHistorico({ acao: "Partes atualizadas", pericia_id: id });
    },
    [id],
  );

  const p = s.pericias.find((x) => x.id === id);
  if (!p) {
    return (
      <div className="surface p-6 text-sm text-cinza" data-ficha-ausente>
        Esta perícia não foi encontrada: ela pode ter sido excluída, ou o link aponta para uma perícia de outra conta.{" "}
        <Link to={`${RAIZ}/pericias`} className="font-semibold text-link hover:underline">Ver as perícias</Link>
      </div>
    );
  }

  const tarefas = s.tarefas.filter((t) => t.pericia_id === id);
  const modelosAtuais = new Set(tarefas.map((t) => t.modelo_id).filter(Boolean));
  const prazos = s.prazos.filter((x) => x.pericia_id === id).sort((a, b) => a.data.localeCompare(b.data));
  const historico = s.historico.filter((h) => h.pericia_id === id);
  const total = p.honorarios.reduce((a, h) => a + h.valor, 0);
  const recebido = p.honorarios.reduce((a, h) => a + h.recebido, 0);
  const pct = total > 0 ? Math.min(100, (recebido / total) * 100) : 0;
  const etapa = etapaNumDe(p);
  const escrever = s.podeEscrever;

  const adicionarPrazo = () => {
    if (!novoPrazoTitulo.trim() || !novoPrazoData) return;
    s.addPrazo({ pericia_id: id, titulo: novoPrazoTitulo.trim(), data: novoPrazoData });
    s.addHistorico({ acao: `Prazo criado: ${novoPrazoTitulo.trim()}`, pericia_id: id });
    setNovoPrazoTitulo("");
    setNovoPrazoData("");
  };
  const adicionarTarefa = () => {
    if (!novaTarefa.trim()) return;
    s.addTarefa({ pericia_id: id, titulo: novaTarefa.trim(), prioridade: "media" });
    setNovaTarefa("");
  };
  const excluir = () => {
    s.deletePericia(p.id);
    setExcluindo(false);
    navegar(`${RAIZ}/pericias`);
  };

  const contagem: Record<Aba, number | null> = {
    prazos: prazos.length, partes: p.partes.length, tarefas: tarefas.length, financeiro: p.honorarios.length, documentos: s.documentos.filter((d) => d.pericia_id === id).length, historico: null,
  };
  const ROTULO_DA_ABA: Record<Aba, string> = { prazos: "Prazos", partes: "Partes", tarefas: "Tarefas", financeiro: "Financeiro", documentos: "Documentos", historico: "Histórico" };

  return (
    <div className="flex flex-col gap-5" data-ficha={p.id}>
      <Cabine trilha={{ para: `${RAIZ}/pericias`, rotulo: "Perícias" }} rotulo={p.tipoPericia || "Perícia"} titulo={nomeDaPericia(p)}
        descricao={<span className="tabular-nums">{[p.numeroProcesso || "(sem número)", p.tribunal, p.vara].filter(Boolean).join(" · ")}</span>}
        acao={escrever && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => s.toggleFavorito(p.id)} aria-pressed={p.favorito}
              aria-label={p.favorito ? "Tirar dos favoritos" : "Marcar como favorita"}
              className="grid size-11 place-items-center rounded-full border-[1.5px] border-white/20 text-navy-ink transition hover:border-laranja">
              <IconeEstrela cheia={p.favorito} className={`size-4 ${p.favorito ? "text-laranja" : ""}`} />
            </button>
            <button type="button" onClick={() => setEditando(true)} className="btn-contorno-navy text-sm" data-editar-pericia>Editar a perícia</button>
            <button type="button" onClick={() => setExcluindo(true)} className="btn-contorno-navy text-sm" data-excluir-pericia>Excluir</button>
          </div>
        )}
        fatos={<>
          <Fato rotulo="Nomeação">{formatarData(p.dataNomeacao)}</Fato>
          <Fato rotulo="Entrega do laudo">{formatarData(p.dataEntregaLaudo)}</Fato>
          <Fato rotulo="Honorários">
            {total > 0 ? (
              <>
                {formatBRL(recebido)} <span className="font-normal text-navy-mute">de {formatBRL(total)}</span>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/15" role="img" aria-label={`${Math.round(pct)}% recebido`}>
                  <div className="h-full rounded-full bg-laranja" style={{ width: `${pct}%` }} />
                </div>
              </>
            ) : <span className="font-normal text-navy-mute">Nenhum lançado</span>}
          </Fato>
          <Fato rotulo="Atualizada">{formatarDataHora(p.atualizadoEm)}</Fato>
        </>} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="surface flex flex-col gap-3 p-4" data-bloco-etapa>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Rotulo>Etapa do processo</Rotulo>
            <Chip tom={PRIORIDADE_TOM[p.prioridade]}>{PRIORIDADE_LABEL[p.prioridade]}</Chip>
          </div>
          <EtapaProgresso pericia={p} grande />
          {escrever && (
            <select className={CAMPO} value={etapa} aria-label="Mudar a etapa" onChange={(e) => s.moverEtapa(p.id, Number(e.target.value) as EtapaNum)} data-mudar-etapa>
              {ETAPAS.map((e) => <option key={e} value={e}>{e}. {ETAPA_LABEL[e]}</option>)}
            </select>
          )}
        </div>
        <ProximaAcaoInline pericia={p} />
      </div>

      <div className="-mx-1 overflow-x-auto px-1">
        <div role="tablist" aria-label="Seções da perícia" className="flex min-w-max gap-1.5">
          {ABAS.map((a) => (
            <button key={a} type="button" role="tab" aria-selected={aba === a} onClick={() => trocarAba(a)}
              className={`min-h-10 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition ${aba === a ? "bg-acento text-acento-tinta" : "border border-borda bg-superficie text-cinza hover:text-titulo"}`}
              data-aba={a}>
              {ROTULO_DA_ABA[a]}{contagem[a] !== null && <span className="ml-1 tabular-nums opacity-80">({contagem[a]})</span>}
            </button>
          ))}
        </div>
      </div>

      <div role="tabpanel" aria-label={ROTULO_DA_ABA[aba]} className="flex flex-col gap-3" data-painel={aba}>
        {aba === "prazos" && (
          <>
            {escrever && (
              <Secao titulo="Novo prazo">
                <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); adicionarPrazo(); }}>
                  <label className="flex min-w-55 flex-1 flex-col gap-1 text-sm font-semibold text-tinta">Título
                    <input className={CAMPO} value={novoPrazoTitulo} maxLength={300} placeholder="Ex.: Entrega do laudo" onChange={(e) => setNovoPrazoTitulo(e.target.value)} data-novo-prazo-titulo />
                  </label>
                  <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Data
                    <input className={CAMPO} type="date" value={novoPrazoData} onChange={(e) => setNovoPrazoData(e.target.value)} data-novo-prazo-data />
                  </label>
                  <button type="submit" className="pill-cta min-h-12 px-5 text-sm" data-incluir-prazo><IconeMais /> Incluir</button>
                  <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setPrazoNovo(true)}>Com observação</button>
                </form>
              </Secao>
            )}
            {prazos.length === 0 && <p className="py-4 text-center text-sm text-cinza">Nenhum prazo nesta perícia ainda.</p>}
            {prazos.map((pr) => {
              const d = diasAte(pr.data);
              return (
                <div key={pr.id} className={`surface flex items-center gap-3 p-4 ${pr.cumprido ? "opacity-80" : ""}`} data-prazo={pr.id}>
                  <input type="checkbox" className="size-4 shrink-0 accent-acento" checked={pr.cumprido} disabled={!escrever}
                    aria-label={`Prazo cumprido: ${pr.titulo}`} onChange={(e) => s.updatePrazo(pr.id, { cumprido: e.target.checked })} />
                  <div className="min-w-0 flex-1">
                    <div className={`truncate font-semibold ${pr.cumprido ? "text-cinza line-through" : "text-titulo"}`}>{pr.titulo}</div>
                    <div className="text-xs text-cinza tabular-nums">{formatarData(pr.data)}{pr.observacao ? ` · ${pr.observacao}` : ""}</div>
                  </div>
                  {!pr.cumprido && <Chip tom={tomDosDias(d)}>{rotuloDias(d)}</Chip>}
                  {escrever && <button type="button" className={BOTAO_ICONE} onClick={() => setPrazoEdit(pr)} aria-label={`Editar ${pr.titulo}`}>✎</button>}
                </div>
              );
            })}
          </>
        )}

        {aba === "partes" && (
          <Secao titulo="Partes do processo" descricao={escrever ? "As mudanças se gravam sozinhas quando você para de digitar." : undefined}>
            {escrever ? (
              <PartesEditor valor={partesEmEdicao ?? p.partes} aoMudar={alterarPartes} />
            ) : p.partes.length === 0 ? (
              <p className="text-sm text-cinza">Nenhuma parte cadastrada.</p>
            ) : (
              <ul className="divide-y divide-borda">{p.partes.map((x) => <li key={x.id} className="py-2 text-sm text-tinta">{x.nome} <span className="text-cinza">· {x.contato}</span></li>)}</ul>
            )}
          </Secao>
        )}

        {aba === "tarefas" && (
          <>
            {escrever && (
              <Secao titulo="Incluir tarefas" descricao="As tarefas de cada etapa entram sozinhas quando a perícia muda de etapa. Use os modelos para incluir outras, ou para recolocar uma que você tirou.">
                <div className="flex flex-wrap gap-2">
                  {TEMPLATES_TAREFA.map((m) => {
                    const ja = modelosAtuais.has(m.id);
                    return (
                      <button key={m.id} type="button" disabled={ja} title={ja ? "Esta tarefa já está na perícia" : m.descricao}
                        className={`${BOTAO_SECUNDARIO} min-h-9 text-xs`} onClick={() => s.criarTarefaDeModelo(m.id, id)} data-modelo={m.id}>
                        {ja ? "✓" : "+"} {m.etapa ? `${m.etapa}. ` : ""}{m.titulo}
                      </button>
                    );
                  })}
                </div>
                <form className="mt-5 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); adicionarTarefa(); }}>
                  <label className="flex min-w-55 flex-1 flex-col gap-1 text-sm font-semibold text-tinta">Nova tarefa avulsa
                    <input className={CAMPO} value={novaTarefa} maxLength={300} placeholder="Ex.: Pedir extratos ao banco" onChange={(e) => setNovaTarefa(e.target.value)} data-nova-tarefa />
                  </label>
                  <button type="submit" className="pill-cta min-h-12 px-5 text-sm" data-incluir-tarefa><IconeMais /> Incluir</button>
                  <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setTarefaNova(true)}>Completa</button>
                </form>
              </Secao>
            )}
            {tarefas.length === 0 && <p className="py-4 text-center text-sm text-cinza">Nenhuma tarefa nesta perícia.</p>}
            {tarefas.map((t) => {
              const feita = t.status === "concluida";
              return (
                <div key={t.id} className="surface flex items-start gap-3 p-4" data-tarefa={t.id} data-modelo-da-tarefa={t.modelo_id ?? ""}>
                  <input type="checkbox" className="mt-1 size-4 shrink-0 accent-acento" checked={feita} disabled={!escrever}
                    aria-label={`Tarefa concluída: ${t.titulo}`} onChange={(e) => s.updateTarefa(t.id, { status: e.target.checked ? "concluida" : "pendente" })} data-concluir />
                  <div className="min-w-0 flex-1">
                    <div className={`font-semibold ${feita ? "text-cinza line-through" : "text-titulo"}`}>{t.titulo}</div>
                    {t.prazo && <div className="text-xs text-cinza tabular-nums">Prazo: {formatarData(t.prazo)}</div>}
                    {t.prazoSugerido && !feita && (
                      escrever ? (
                        <button type="button" onClick={() => setTarefaEdit(t)} className="mt-1.5 text-left text-xs font-semibold text-alerta-tinta hover:underline">
                          Prazo sugerido. Ajuste conforme o prazo definido na sua perícia.
                        </button>
                      ) : <p className="mt-1.5 text-xs font-semibold text-alerta-tinta">Prazo sugerido.</p>
                    )}
                    {t.checklist.length > 0 && (
                      <div className="mt-3 flex flex-col gap-2">
                        {t.checklist.map((c) => (
                          <label key={c.id} className="flex items-center gap-2.5 text-sm text-tinta">
                            <input type="checkbox" className="size-4 shrink-0 accent-acento" checked={c.feito} disabled={!escrever}
                              onChange={() => s.toggleChecklist(t.id, c.id)} data-item={c.id} />
                            <span className={c.feito ? "text-cinza line-through" : ""}>{c.texto}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <Chip tom={PRIORIDADE_TOM[t.prioridade]}>{PRIORIDADE_LABEL[t.prioridade]}</Chip>
                  {escrever && <button type="button" className={BOTAO_ICONE} onClick={() => setTarefaEdit(t)} aria-label={`Editar ${t.titulo}`}>✎</button>}
                </div>
              );
            })}
          </>
        )}

        {aba === "financeiro" && (
          <Secao titulo="Honorários" descricao="O valor, o que entrou, as retenções e as datas."
            acao={escrever && <button type="button" className="pill-cta min-h-10 px-5 text-sm" onClick={() => setHonorNovo(true)} data-novo-honorario><IconeMais /> Novo honorário</button>}>
            {p.honorarios.length === 0 ? (
              <p className="rounded-cf-lg border border-dashed border-borda py-6 text-center text-sm text-cinza">Nenhum honorário lançado. Lance os honorários arbitrados para acompanhar o que entra e o que falta receber.</p>
            ) : (
              <ul className="divide-y divide-borda">
                {p.honorarios.map((h) => (
                  <li key={h.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0" data-honorario={h.id}>
                    <div className="min-w-45 flex-1">
                      <div className="font-semibold text-titulo">{h.descricao || "Honorário"}</div>
                      <div className="text-xs text-cinza tabular-nums">
                        Previsto para {formatarData(h.vencimento)}
                        {h.dataRecebimento && <> · recebido em {formatarData(h.dataRecebimento)}</>}
                        {(h.inss || h.irrf) && <> · INSS {formatBRL(h.inss ?? 0)} · IRRF {formatBRL(h.irrf ?? 0)}</>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-titulo tabular-nums">{formatBRL(h.recebido)} <span className="font-normal text-cinza">de {formatBRL(h.valor)}</span></div>
                      <Chip tom={PAGAMENTO_TOM[h.status]}>{PAGAMENTO_LABEL[h.status]}</Chip>
                    </div>
                    {escrever && <button type="button" className={BOTAO_ICONE} onClick={() => setHonorEdit(h)} aria-label={`Editar ${h.descricao || "o honorário"}`}>✎</button>}
                  </li>
                ))}
              </ul>
            )}
          </Secao>
        )}

        {aba === "documentos" && (
          <Secao titulo="Documentos" descricao="Os arquivos desta perícia. Só você os abre.">
            <DocumentosDaPericia periciaId={p.id} escrever={escrever} />
          </Secao>
        )}

        {aba === "historico" && (
          <Secao titulo="Histórico" descricao="O que mudou nesta perícia, do mais recente ao mais antigo.">
            {historico.length === 0 ? (
              <p className="text-sm text-cinza">Sem registros ainda.</p>
            ) : (
              // A linha do tempo: um trilho à esquerda com um ponto por registro.
              <ol className="relative flex flex-col gap-4 border-l-2 border-borda pl-5" data-historico>
                {historico.map((h) => (
                  <li key={h.id} className="relative">
                    <span aria-hidden="true" className="absolute top-1.5 -left-[27px] size-3 rounded-full border-2 border-superficie bg-laranja" />
                    <div className="text-sm font-semibold text-titulo">{h.acao}</div>
                    <div className="text-xs text-cinza tabular-nums">{formatarDataHora(h.ts)}{h.detalhe ? ` · ${h.detalhe}` : ""}</div>
                  </li>
                ))}
              </ol>
            )}
          </Secao>
        )}
      </div>

      <PericiaDialogo pericia={p} aberto={editando} aoFechar={() => setEditando(false)} />
      <PrazoDialogo prazo={prazoEdit} aberto={!!prazoEdit || prazoNovo} periciaPadrao={p.id} aoFechar={() => { setPrazoEdit(null); setPrazoNovo(false); }} />
      <TarefaDialogo tarefa={tarefaEdit} aberto={!!tarefaEdit || tarefaNova} periciaPadrao={p.id} aoFechar={() => { setTarefaEdit(null); setTarefaNova(false); }} />
      <HonorarioDialogo pericia={p} honorario={honorEdit} aberto={!!honorEdit || honorNovo} aoFechar={() => { setHonorEdit(null); setHonorNovo(false); }} />
      <Dialogo aberto={excluindo} aoFechar={() => setExcluindo(false)} titulo="Excluir esta perícia?"
        rodape={<>
          <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setExcluindo(false)}>Cancelar</button>
          <button type="button" className={BOTAO_PERIGO} onClick={excluir} data-confirmar-exclusao>Excluir a perícia</button>
        </>}>
        <p className="text-sm leading-relaxed text-tinta">
          Os honorários e os documentos dela saem junto, e não há como desfazer. As tarefas e os prazos ficam guardados, sem vínculo com perícia nenhuma.
        </p>
      </Dialogo>
    </div>
  );
}
